import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as teamsApi from './api'
import type { CreateTeamRequest, UpdateTeamRequest } from './types'

export const teamKeys = {
  list: (activeOnly: boolean) => ['admin', 'teams', activeOnly] as const,
  members: (teamId: number) => ['admin', 'teams', 'members', teamId] as const,
  mine: () => ['teams', 'me'] as const,
}

export function useTeams(activeOnly = true) {
  return useQuery({
    queryKey: teamKeys.list(activeOnly),
    queryFn: () => teamsApi.listTeams(activeOnly),
  })
}

/**
 * Teams the current user belongs to (own + descendants).
 * Cached 5 min - membership changes are rare and any UI mutation can manually invalidate.
 */
export function useMyTeams() {
  return useQuery({
    queryKey: teamKeys.mine(),
    queryFn: teamsApi.listMyTeams,
    staleTime: 5 * 60 * 1000,
  })
}

export function useTeamMembers(teamId: number | null | undefined) {
  return useQuery({
    queryKey: teamKeys.members(teamId ?? -1),
    queryFn: () => teamsApi.listMembers(teamId as number),
    enabled: typeof teamId === 'number' && teamId > 0,
  })
}

function invalidateTeams(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['admin', 'teams'] })
}

export function useCreateTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateTeamRequest) => teamsApi.createTeam(payload),
    onSuccess: () => invalidateTeams(qc),
  })
}

export function useUpdateTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: number; payload: UpdateTeamRequest }) =>
      teamsApi.updateTeam(vars.id, vars.payload),
    onSuccess: () => invalidateTeams(qc),
  })
}

export function useDeactivateTeam() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => teamsApi.deactivateTeam(id),
    onSuccess: () => invalidateTeams(qc),
  })
}

export function useAddMember(teamId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => teamsApi.addMember(teamId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(teamId) }),
  })
}

export function useRemoveMember(teamId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => teamsApi.removeMember(teamId, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: teamKeys.members(teamId) }),
  })
}
